import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import StripeConfig from './StripeConfig'; // Ensure this component exists
import SmtpConfig from './SmtpConfig';   // Ensure this component exists
import PlanSettings from './PlanSettings';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('api'); // Single state for all tabs
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  // API Settings State
  const [pneumoniaApi, setPneumoniaApi] = useState({ url: '' });
  const [mriApi, setMriApi] = useState({ url: '' });
  const [apiSuccess, setApiSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  // Frontend Settings State
  const [landingPageContent, setLandingPageContent] = useState({
    bannerTitle: 'Rib Cage Bones Human Anatomy',
    bannerDescription:
      'Discover advanced X-ray analysis for precise pneumonia detection. Our platform uses cutting-edge technology to enhance medical diagnostics, ensuring accuracy and efficiency for healthcare professionals worldwide.',
    bannerImage: 'https://www.kindpng.com/picc/m/71-714262_vector-illustration-of-health-care-professional-doctor-x.png',
    feature1: 'Get Fast Result Just one Click',
    feature2: 'High Accuracy Case Prediction',
    feature3: 'Best ML Model for X-ray and MRI',
    feature4: 'Secure Data No Need Details',
    accuracyPneumonia: 95,
    accuracyBrainTumor: 99,
    accuracyDescription:
      'Our state-of-the-art diagnostic model ensures precision with robust datasets. For pneumonia, we trained on over 6,000 high-resolution X-ray images, achieving 95% accuracy. For brain tumors, 7,035 MRI/CT scans yielded 99% accuracy. Expert-curated, these datasets use CNNs and transfer learning. Cross-validation and data augmentation minimize bias, ensuring reliable diagnostics. This empowers healthcare providers with actionable insights for critical decisions.',
  });
  const [frontendSuccess, setFrontendSuccess] = useState('');
  const [frontendError, setFrontendError] = useState('');

  // Check authentication and admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        await currentUser.getIdToken(true); // Refresh token
        setUser(currentUser);
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          if (userSnap.data().role === 'admin') {
            setIsAdmin(true);
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/');
        }
      } else {
        navigate('/login'); // Redirect to login if no user
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Load API settings from Firestore
  useEffect(() => {
    if (!isAdmin) return;

    const fetchApiSettings = async () => {
      try {
        const apiSettingsRef = doc(db, 'settings', 'api');
        const apiSettingsSnap = await getDoc(apiSettingsRef);
        if (apiSettingsSnap.exists()) {
          const data = apiSettingsSnap.data();
          setPneumoniaApi(data.pneumoniaApi || { url: '' });
          setMriApi(data.mriApi || { url: '' });
        } else {
          await setDoc(apiSettingsRef, {
            pneumoniaApi: { url: '' },
            mriApi: { url: '' },
          });
        }
      } catch (err) {
        console.error('Error loading API settings:', err);
      }
    };

    fetchApiSettings();
  }, [isAdmin]);

  // Load LandingPage content from Firestore
  useEffect(() => {
    if (!isAdmin) return;

    const fetchLandingPageContent = async () => {
      try {
        const landingPageRef = doc(db, 'settings', 'landing_page');
        const landingPageSnap = await getDoc(landingPageRef);
        if (landingPageSnap.exists()) {
          setLandingPageContent(landingPageSnap.data());
        } else {
          await setDoc(landingPageRef, landingPageContent);
        }
      } catch (err) {
        console.error('Error loading LandingPage content:', err);
      }
    };

    fetchLandingPageContent();
  }, [isAdmin]);

  // Save API settings to Firestore
  const handleSaveApiSettings = async () => {
    setApiSuccess('');
    setApiError('');
    try {
      const apiSettingsRef = doc(db, 'settings', 'api');
      await setDoc(apiSettingsRef, { pneumoniaApi, mriApi }, { merge: true });
      setApiSuccess('API settings saved successfully!');
    } catch (err) {
      setApiError('Failed to save API settings: ' + err.message);
    }
  };

  // Save LandingPage content to Firestore
  const handleSaveLandingPageContent = async () => {
    setFrontendSuccess('');
    setFrontendError('');
    try {
      const landingPageRef = doc(db, 'settings', 'landing_page');
      await setDoc(landingPageRef, landingPageContent, { merge: true });
      setFrontendSuccess('LandingPage content saved successfully!');
    } catch (err) {
      setFrontendError('Failed to save LandingPage content: ' + err.message);
    }
  };

  // Save SMTP settings to Firestore
  const handleSaveSmtpSettings = async (config) => {
    const smtpDocRef = doc(db, 'smtpSettings', 'config');
    await setDoc(smtpDocRef, config, { merge: true });
  };

  // Save Stripe settings to Firestore
  const handleSaveStripeSettings = async (config) => {
    const stripeDocRef = doc(db, 'stripeSettings', 'config');
    await setDoc(stripeDocRef, config, { merge: true });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-600">Loading...</div>;
  if (!user || !isAdmin) return <div className="min-h-screen flex items-center justify-center text-red-600">Access denied. Admins only.</div>;

  return (
    <div className="settings p-6 font-['Noto Sans'] min-h-screen">
      <h2 className="text-4xl font-bold mb-6 text-gray-600">Settings</h2>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-300">
        <button
          onClick={() => setActiveTab('api')}
          className={`mr-4 pb-2 text-lg font-semibold ${activeTab === 'api' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}
        >
          API Settings
        </button>
        <button
          onClick={() => setActiveTab('frontend')}
          className={`mr-4 pb-2 text-lg font-semibold ${activeTab === 'frontend' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}
        >
          Frontend Settings
        </button>
        <button
          onClick={() => setActiveTab('smtp')}
          className={`mr-4 pb-2 text-lg font-semibold ${activeTab === 'smtp' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}
        >
          Email SMTP
        </button>
        <button
          onClick={() => setActiveTab('payment')}
          className={`mr-4 pb-2 text-lg font-semibold ${activeTab === 'payment' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}
        >
          Payment Settings
        </button>
        <button
          onClick={() => setActiveTab('plans')}
          className={`mr-4 pb-2 text-lg font-semibold ${activeTab === 'plans' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'}`}
        >
          Plan Settings
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'api' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {apiSuccess && (
            <div className="col-span-2 mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
              {apiSuccess}
            </div>
          )}
          {apiError && (
            <div className="col-span-2 mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {apiError}
            </div>
          )}
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
            <h3 className="text-2xl font-bold mb-4 text-gray-600">Pneumonia X-ray API</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 mb-1">API URL</label>
                <input
                  type="text"
                  value={pneumoniaApi.url}
                  onChange={(e) => setPneumoniaApi({ ...pneumoniaApi, url: e.target.value })}
                  className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., https://xray-model-service-1029646538571.us-central1.run.app/predict"
                />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
            <h3 className="text-2xl font-bold mb-4 text-gray-600">MRI Tumors of Brain API</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 mb-1">API URL</label>
                <input
                  type="text"
                  value={mriApi.url}
                  onChange={(e) => setMriApi({ ...mriApi, url: e.target.value })}
                  className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., https://mri-model-service-1029646538571.us-central1.run.app/predict"
                />
              </div>
            </div>
          </div>
          <div className="col-span-2 flex justify-end">
            <button
              onClick={handleSaveApiSettings}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300 font-semibold"
            >
              Save API Settings
            </button>
          </div>
        </div>
      )}

      {activeTab === 'frontend' && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
          {frontendSuccess && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
              {frontendSuccess}
            </div>
          )}
          {frontendError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {frontendError}
            </div>
          )}
          <h3 className="text-2xl font-bold mb-4 text-gray-600">Frontend Settings (Landing Page)</h3>
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-600 mb-2">Main Banner</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600 mb-1">Banner Title</label>
                  <input
                    type="text"
                    value={landingPageContent.bannerTitle}
                    onChange={(e) => setLandingPageContent({ ...landingPageContent, bannerTitle: e.target.value })}
                    className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Banner Description</label>
                  <textarea
                    value={landingPageContent.bannerDescription}
                    onChange={(e) => setLandingPageContent({ ...landingPageContent, bannerDescription: e.target.value })}
                    className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="4"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Banner Image URL</label>
                  <input
                    type="text"
                    value={landingPageContent.bannerImage}
                    onChange={(e) => setLandingPageContent({ ...landingPageContent, bannerImage: e.target.value })}
                    className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-600 mb-2">Features Section</h4>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((num) => (
                  <div key={num}>
                    <label className="block text-gray-600 mb-1">Feature {num}</label>
                    <input
                      type="text"
                      value={landingPageContent[`feature${num}`]}
                      onChange={(e) => setLandingPageContent({ ...landingPageContent, [`feature${num}`]: e.target.value })}
                      className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-600 mb-2">Prediction Accuracy Section</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-600 mb-1">Pneumonia Accuracy (%)</label>
                  <input
                    type="number"
                    value={landingPageContent.accuracyPneumonia}
                    onChange={(e) => setLandingPageContent({ ...landingPageContent, accuracyPneumonia: Number(e.target.value) })}
                    className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Brain Tumor Accuracy (%)</label>
                  <input
                    type="number"
                    value={landingPageContent.accuracyBrainTumor}
                    onChange={(e) => setLandingPageContent({ ...landingPageContent, accuracyBrainTumor: Number(e.target.value) })}
                    className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">Accuracy Description</label>
                  <textarea
                    value={landingPageContent.accuracyDescription}
                    onChange={(e) => setLandingPageContent({ ...landingPageContent, accuracyDescription: e.target.value })}
                    className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="6"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveLandingPageContent}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300 font-semibold"
              >
                Save Frontend Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'smtp' && (
        <SmtpConfig isAdmin={isAdmin} onSave={handleSaveSmtpSettings} />
      )}

      {activeTab === 'payment' && (
        <StripeConfig isAdmin={isAdmin} onSave={handleSaveStripeSettings} />
      )}

      {activeTab === 'plans' && (
        <PlanSettings />
      )}
    </div>
  );
};

export default Settings;